import { difference, values, flatMap, flatten, groupBy } from 'lodash';
import { isReplyStateIN, } from './reply-types';
import { isDefined, isOfType, unreachable } from '../../../../ts-utils';
import { getNextReplyChunkState, getPrevReplyChunkState, } from '../paging/comments-paging-selectors';
import { getReactionsState } from '../reactions/comments-reaction-selectors';
import { getMarkedState } from '../mark/mark-selectors';
import { isMarkable } from '../mark/mark-types';
export const getReplyListState = (replyListsStateIN, commentsById) => {
    if (!replyListsStateIN) {
        return undefined;
    }
    const replyIds = getReplyListIds(replyListsStateIN, replyListsStateIN.newRepliesToReplies);
    const next = getNextReplyChunkState(replyListsStateIN);
    const prev = getPrevReplyChunkState(replyListsStateIN);
    const parentCommentId = replyListsStateIN.parentCommentId;
    if (replyIds.length === 0) {
        if (next.type === 'NONE') {
            return replyListsStateIN.liveRepliesPurgatory.length === 0
                ? {
                    type: 'EMPTY',
                    parentCommentId,
                }
                : {
                    type: 'ONLY_VIEW_LIVE',
                    parentCommentId,
                    count: replyListsStateIN.liveRepliesPurgatory.length,
                };
        }
        else {
            return replyListsStateIN.liveRepliesPurgatory.length === 0
                ? {
                    type: 'ONLY_LOAD_MORE',
                    parentCommentId,
                    next,
                }
                : {
                    type: 'ONLY_LOAD_MORE_AND_LIVE',
                    parentCommentId,
                    next,
                };
        }
    }
    const comments = replyIds.map((id) => commentsById[id]);
    return {
        type: 'FULL',
        parentCommentId,
        comments,
        next,
        prev,
        live: replyListsStateIN.liveRepliesPurgatory.length
            ? { type: 'SOME', count: replyListsStateIN.liveRepliesPurgatory.length }
            : { type: 'NONE' },
    };
};
export const mapReplyState = (replyStateIN, memberState) => {
    const reactions = 'reactions' in replyStateIN
        ? getReactionsState(replyStateIN.reactions, memberState)
        : undefined;
    const marked = isMarkable(replyStateIN)
        ? getMarkedState(replyStateIN.comment, replyStateIN.marked)
        : undefined;
    return {
        ...replyStateIN,
        ...(reactions && { reactions }),
        ...(isDefined(marked) && { marked }),
    };
};
export const getReplyListIds = (repliesState, newRepliesToReplies) => {
    // We fetch replies sorted with NEWEST_FIRST, but in ui we show them in reverse order.
    // (thats because the reply paging goes 'backwards', and we can page only 'forward')
    const chunks = repliesState.chunks || [];
    const commentIds = flatMap(chunks, (chunk) => {
        switch (chunk.type) {
            case 'REPLY_TARGET':
                return chunk.commentId;
            case 'NEW':
                return chunk.localOrCommentId;
            case 'LIVE':
                return chunk.commentIds;
            case 'FETCHED':
            case 'INITIAL':
                return difference(chunk.commentIds, 
                // To prevent double replies we filter out 'REPLY_TARGET' chunk ids from other chunks
                chunks.filter(isOfType('REPLY_TARGET')).map((c) => c.commentId), 
                // Same with 'LIVE' replies
                flatten(chunks.filter(isOfType('LIVE')).map((c) => c.commentIds)), 
                // New repies might also appear in loaded chunks - for example in blog case, when intialy 0 comments loaded
                flatten(values(newRepliesToReplies)), chunks.filter(isOfType('NEW')).map((c) => c.localOrCommentId), flatten(chunks.filter(isOfType('LIVE')).map((c) => c.commentIds)));
            default:
                throw unreachable(chunk);
        }
    }).reverse();
    return atachNewReplies(commentIds, newRepliesToReplies);
};
// merges new replie ids into fetched replies
const atachNewReplies = (replyIds, newReplies) => {
    // New comments might be replying to each other, so we have to collect them reucursively.
    const getNewRepliesForReplyId = (replyId) => flatMap(newReplies[replyId] || [], (id) => [id, ...getNewRepliesForReplyId(id)]);
    return flatMap(replyIds, (replyId) => [replyId, ...getNewRepliesForReplyId(replyId)]);
};
// Get the id under which the comment is referenced in state - localId for not
// yet created and commentId form server for the created ones
export const getWorkingId = (replyState) => {
    if (isOfType(['REPLY_TYPING', 'REPLY_CREATE_ERROR', 'REPLY_CREATE_PENDING', 'REPLY_CREATE_ERROR_EDITING'], replyState)) {
        return replyState.localId;
    }
    return replyState.commentId;
};
export const getChildrenIds = (commentsById) => {
    const allReplies = values(commentsById).filter(isReplyStateIN);
    const repliesByTarget = groupBy(allReplies, (replyState) => replyState.targetId);
    const _getChildren = (targetId) => {
        return flatMap(repliesByTarget[targetId], (commentState) => {
            if (!isReplyStateIN(commentState)) {
                return [];
            }
            const id = getWorkingId(commentState);
            return [id, ..._getChildren(id)];
        });
    };
    return _getChildren;
};
//# sourceMappingURL=replies-selectors.js.map